import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CartProvider } from "@/contexts/CartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PageTitle } from "@/components/PageTitle";
import { PageSpinner } from "@/components/PageSpinner";
// Eager: the only two possible destinations for every visit to "/" (see the
// root Navigate below and ProtectedRoute's redirect), so lazy-loading either
// would serialize a chunk fetch after the auth check on the app's hottest path.
import Login from "./pages/Login";
import AccountDashboard from "./pages/account/Dashboard";

const Catalog = lazy(() => import("./pages/Catalog"));
const CatalogNode = lazy(() => import("./pages/CatalogNode"));
const Search = lazy(() => import("./pages/Search"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Collection = lazy(() => import("./pages/Collection"));

const Favorites = lazy(() => import("./pages/Favorites"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Delivery = lazy(() => import("./pages/Delivery"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Register = lazy(() => import("./pages/Register"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AccountOrders = lazy(() => import("./pages/account/Orders"));
const AccountDocuments = lazy(() => import("./pages/account/Documents"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

/**
 * Wraps public-only routes (login, register).
 * Redirects already-authenticated users to /account.
 */
function PublicOnlyRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/account" replace />;
  }

  return <Outlet />;
}

/**
 * Resets the error boundary when the URL path changes so navigation can recover
 * from a crashed route without a full reload.
 */
function RoutedContent() {
  const { pathname } = useLocation();

  return (
    <AppErrorBoundary key={pathname}>
      <ScrollToTop />
      <PageTitle />
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<Navigate to="/account" replace />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/delivery" element={<Delivery />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Public-only routes — redirect to /account if already authenticated */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
          </Route>

          {/* Protected routes — redirect to /login if not authenticated */}
          <Route element={<ProtectedRoute />}>
            <Route path="/catalog" element={<Catalog />} />
            {/* Splat, not fixed segments: the catalog tree has no fixed depth. */}
            <Route path="/catalog/*" element={<CatalogNode />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/search" element={<Search />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/collection/:type" element={<Collection />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/account" element={<AccountDashboard />} />
            <Route path="/account/orders" element={<AccountOrders />} />
            <Route path="/account/documents" element={<AccountDocuments />} />
          </Route>

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <FavoritesProvider>
            <TooltipProvider>
              <Sonner />
              <RoutedContent />
            </TooltipProvider>
          </FavoritesProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
